import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { db } from './firebaseConfig';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Layout, CREAM, RED, BLACK } from './components/Layout';
import { ShopPage } from './pages/ShopPage';
import { matchesSearch } from './search';
import { ProductPage } from './pages/ProductPage';
import AccountPage from './pages/AccountPage';
import CartPage from './pages/CartPage';
import { SuccessPage } from './pages/SuccessPage';
import { StaticPage } from './pages/StaticPage';
import { SearchAgent } from './SearchAgent';
import languagesData from './locales/languages.json';
import aboutData from './locales/about.json';

const SMARTSHOP_API = 'https://europe-west1-dbos-core-prod.cloudfunctions.net';

interface LanguageEntry { nav: string[]; hero: string; buy: string; hygiene: string; search_placeholder: string; }
interface Product { gid: string; slug?: string; name: string; category: string; glen_price: number; primary_image: string; secondary_image: string; all_assets?: string[]; featured_sale: boolean; }


const SMART_FILTER_MAP: Record<string, string[]> = {
  'Ukuleles': ['Ukuleles'],
  'Acoustic Guitars': ['Acoustic Guitars', 'Classical Guitars', 'Bass Guitars', 'CigarBox Guitars'],
  'Keyboards & Pianos': ['Keyboards & Pianos'],
  'Traditional & Folk': ['Traditional & Folk', 'Mandolin', 'Banjo', 'Bouzouki', 'Concertina', 'Bodhran', 'Tin Whistles', 'Harps', 'Violins', 'Accordians', 'Violin'],
  'Drum Kit': ['Drums', 'Drum Kit'],
  'Studio & Sound': ['Studio & Sound', 'Microphones', 'Speakers'],
  'Strings': ['Strings', 'Acoustic Strings', 'Electric Strings', 'Classical Strings', 'Bass Strings', 'Ukulele Strings', 'Mandolin Strings', 'Banjo Strings', 'Bouzouki Strings'],
  'Electric Guitars': ['Electric Guitars'],
};

const useImageCycle = (images: string[], active: boolean, interval = 2000) => {
  const [currentImg, setCurrentImg] = React.useState(0);
  const [zoomed, setZoomed] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  React.useEffect(() => {
    if (!active || images.length < 2) {
      if (active && images.length >= 1) setZoomed(true);
      return;
    }
    setZoomed(true);
    timerRef.current = setInterval(() => {
      setZoomed(false);
      setTimeout(() => {
        setCurrentImg(prev => (prev + 1) % images.length);
        setZoomed(true);
      }, 300);
    }, interval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active, images.length, interval]);

  React.useEffect(() => {
    if (!active) {
      if (timerRef.current) clearInterval(timerRef.current);
      setCurrentImg(0);
      setZoomed(false);
    }
  }, [active]);

  return { currentImg, zoomed };
};

const CATEGORIES: { label: string; icon: string }[] = [
  { label: 'Strings', icon: '🎻' },
  { label: 'Ukuleles', icon: '🪕' },
  { label: 'Acoustic Guitars', icon: '🪕' },
  { label: 'Electric Guitars', icon: '🎸' },
  { label: 'Studio & Sound', icon: '🎙️' },
  { label: 'Keyboards & Pianos', icon: '🎹' },
  { label: 'Drum Kit', icon: '🥁' },
];

const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const [hovered, setHovered] = useState(false);
  const allImages = [product.primary_image, product.secondary_image, ...(product.all_assets || [])]
    .filter((img): img is string => !!img)
    .filter((img, i, arr) => arr.indexOf(img) === i)
    .slice(0, 6);
  const { currentImg, zoomed } = useImageCycle(allImages, hovered, 2000);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => { const dest = product.slug || product.gid; if(dest) window.location.href = `/product/${dest}`; console.log('NAV:', dest, product); }}
      style={{
        position: 'relative', overflow: 'hidden', background: 'white', cursor: 'pointer',
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: hovered
          ? '0 0 0 1px rgba(139,26,26,0.2), 0 4px 8px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.14), 0 32px 64px rgba(0,0,0,0.18), 0 0 80px rgba(139,26,26,0.08)'
          : '0 0 0 1px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04), 0 8px 16px rgba(0,0,0,0.07), 0 24px 48px rgba(0,0,0,0.09)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        transition: 'box-shadow 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}>
      <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '4/3', height: 'auto', minHeight: 180, maxHeight: 280, background: 'white' }}>
        {/* Only the first image is in the DOM until the card is hovered. loading="lazy"
            defers images OUTSIDE the viewport - six stacked inside a VISIBLE card all
            download immediately, so a screen of 12 cards fetched up to 72 full-size
            images, over 4 MB on Glen's catalogue, whose migrated originals have no
            renditions. The hover cycle needs the others only once it is running, and by
            then the shopper has declared interest in this one card. */}
        {allImages.map((img, i) => ((i > 0 && !hovered) ? null : (
          <img key={i} src={img} alt={product.name} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: 'white',
            opacity: currentImg === i ? 1 : 0,
            transform: currentImg === i && zoomed ? 'scale(1.10)' : 'scale(1)',
            transition: 'opacity 0.3s ease-in-out, transform 2s ease-in-out',
            zIndex: currentImg === i ? 1 : 0,
          }} />
        )))}
        {allImages.length === 0 && <div style={{ position: 'absolute', inset: 0, background: '#f0ede0' }} />}
      </div>
      <div style={{ padding: '14px 16px 18px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
        <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,0,0,0.35)', margin: '0 0 4px' }}>{product.category}</p>
        <p style={{ fontWeight: 700, fontSize: 13, color: BLACK, margin: '0 0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</p>
        <p style={{ fontWeight: 900, fontSize: 16, color: RED, margin: 0 }}>€{product.glen_price?.toFixed(2)}</p>
      </div>
    </div>
  );
};

const FeaturedProducts: React.FC<{ activeCategory: string | null }> = ({ activeCategory }) => {
  const [homeSearch, setHomeSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        // Native read path - same endpoint ShopPage uses. NOT a direct Firestore
        // read: a browser read is governed by rules and breaks on every rules
        // deploy (D-008/D-009). The function runs as a service account and does not.
        const res = await fetch(`${SMARTSHOP_API}/smartshop-list-products`, {
          // view=grid: the home grid shows name, price and image. The full shape also
          // carries description, technical_specs, options and variants - about a third
          // of the payload - for fields no card reads. ShopPage has used grid since it
          // was added; home was still asking for everything.
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ view: 'grid' }),
        });
        const data = await res.json();
        const prods = (data.products || []).map((p: any) => ({
          gid: p.gid, slug: p.slug, name: p.name, category: p.category,
          glen_price: p.price, primary_image: p.primary_image, secondary_image: p.secondary_image,
          all_assets: p.all_assets, stock_status: p.stock_status, description: p.description,
          published: p.status === 'active',
          featured_sale: p.featured_sale ?? false,
        } as Product));
        // No client-side sort. smartshop-list-products returns D-131 order -
        // featured, then newest day, then alphabetical. A rendering that
        // re-derives the order has forked the surface (clause 1), and that is
        // exactly why home and shop showed different orders for months: each
        // page invented its own rule and neither used the shop's own.
        setProducts(prods);
      } catch(e) { console.error(e); } finally { setLoading(false); }
    })();
  }, []);
  const filtered = activeCategory
    ? products.filter(p => {
        const cats = SMART_FILTER_MAP[activeCategory] || [activeCategory];
        return cats.includes(p.category);
      })
    : products;

  // --- grid pagination (Phase-3 SmartShop standard: callback-ref IntersectionObserver) ---
  // Same pattern as ShopPage. A callback ref (not useRef+effect) attaches the observer
  // on the node's first mount, so it works on first load, not only after re-navigating.
  // PAGE is 8, not 24: each card costs one image request, and Glen's migrated originals
  // have no renditions, so 24 cards put 24 full-size images in flight in the same tick -
  // measured over 4 MB, with the first image arriving no sooner than the last. Eight gets
  // the shopper a readable shop in one screen's worth of bytes.
  // rootMargin must stay well under one page of cards. At 600px the sentinel sat inside
  // the band after an 8-card page, re-fired immediately and cascaded back to 24.
  const PAGE = 8;
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (node) {
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) setVisibleCount((n) => n + PAGE);
      }, { rootMargin: '200px' });
      observerRef.current.observe(node);
    }
  }, []);
  // reset to the first page whenever the category filter or the home search changes
  useEffect(() => { setVisibleCount(PAGE); }, [activeCategory, homeSearch]);

  const shown = homeSearch.trim()
    ? filtered.filter(p => matchesSearch(p, homeSearch))
    : filtered;

  if (loading || filtered.length === 0) return null;
  return (
    <section style={{ padding: '48px 40px 64px', background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(139,26,26,0.07) 0%, rgba(244,241,225,0) 65%), radial-gradient(ellipse 60% 40% at 50% 0%, rgba(139,26,26,0.04) 0%, rgba(244,241,225,0) 60%), #F4F1E1' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, color: 'rgba(0,0,0,0.4)', margin: 0 }}>Featured Selection</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input type="text" placeholder="Search products..." value={homeSearch} onChange={e => setHomeSearch(e.target.value)}
            style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)', padding: '7px 14px', fontSize: 12, outline: 'none', width: 200, fontFamily: 'inherit' }} />
          <a href="/shop" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: RED, textDecoration: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>View All →</a>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
        {shown.slice(0, visibleCount).map(p => <ProductCard key={p.gid} product={p} />)}
      </div>
      {visibleCount < shown.length && (
        // key forces a remount on each growth - see ShopPage.tsx
        <div key={visibleCount} ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
      )}
    </section>
  );
};

const SmartFilterDropdown: React.FC<{ activeCategory: string | null; setActiveCategory: (c: string | null) => void; onNavigate?: (cat: string | null) => void }> = ({ activeCategory, setActiveCategory, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [showTip, setShowTip] = useState(false);
  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: 'rgba(0,0,0,0.4)' }}>Most Loved</span>
        <div style={{ position: 'relative', display: 'inline-block' }}
          onMouseEnter={() => setShowTip(true)} onMouseLeave={() => setShowTip(false)}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'rgba(0,0,0,0.15)', fontSize: 10, fontWeight: 700, color: 'rgba(0,0,0,0.5)', cursor: 'help', fontStyle: 'italic' }}>i</span>
          {showTip && (
            <div style={{ position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)', background: BLACK, color: CREAM, fontSize: 11, lineHeight: 1.5, padding: '10px 14px', borderRadius: 4, width: 220, zIndex: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', pointerEvents: 'none' }}>
              These are the most popular instrument categories chosen by visitors to this site. The filter updates automatically based on browsing behaviour.
              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderWidth: 5, borderStyle: 'solid', borderColor: BLACK + ' transparent transparent transparent' }} />
            </div>
          )}
        </div>
      </div>
      <button onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: BLACK, color: CREAM, border: 'none', padding: '12px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>{activeCategory ? CATEGORIES.find(c => c.label === activeCategory)?.icon : '🪉'}</span>
          {activeCategory || 'All Instruments'}
        </span>
        <span style={{ fontSize: 10, opacity: 0.6 }}>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: BLACK, border: '1px solid rgba(255,255,255,0.1)', zIndex: 100 }}>
          <button onClick={() => { setActiveCategory(null); setOpen(false); if(onNavigate) onNavigate(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 16px', background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', color: !activeCategory ? RED : 'rgba(255,255,255,0.80)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', fontWeight: !activeCategory ? 700 : 500 }}>
            <span style={{ fontSize: 18 }}>🪉</span> All Instruments
          </button>
          {CATEGORIES.map(cat => (
            <button key={cat.label} onClick={() => { const next = activeCategory === cat.label ? null : cat.label; setActiveCategory(next); setOpen(false); if(onNavigate) onNavigate(next); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 16px', background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', color: activeCategory === cat.label ? RED : 'rgba(255,255,255,0.80)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', fontWeight: activeCategory === cat.label ? 700 : 500 }}>
              <span style={{ fontSize: 18 }}>{cat.icon}</span> {cat.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const HomePage: React.FC<{ lang: string }> = ({ lang }) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const navigate = useNavigate();
  const languages = languagesData as Record<string, LanguageEntry>;
  const t = languages[lang] || languages['EN'];

  return (
    <>
      <style>{`
        @media(max-width:768px) {
          #hero-grid { flex-direction: column !important; }
          #agent-col { border-left: none !important; border-top: 1px solid rgba(0,0,0,0.08) !important; padding-left: 0 !important; padding-top: 24px !important; margin-top: 24px !important; }
        }
      `}</style>

      <section style={{ position: 'relative', padding: '52px 40px 0', backgroundImage: 'url(https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/hero/shop-front-essaness-music-new.jpg)', backgroundSize: 'cover', backgroundPosition: 'center top' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(244,241,225,0.82)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontWeight: 900, fontStyle: 'italic', lineHeight: 1.05, color: BLACK, fontSize: 'clamp(1.3rem,2.2vw,2.1rem)', margin: '0 0 14px' }}>{t.hero}</h1>
          <div style={{ width: 52, height: 4, background: RED, margin: '0 0 40px' }} />
          <div id="hero-grid" style={{ display: 'flex', gap: 48, alignItems: 'flex-start', paddingBottom: 0, borderBottom: 'none' }}>
            <div style={{ flex: 1.2, minWidth: 0 }}>
              <p style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.75)', margin: 0, fontWeight: 700 }}>
                {(aboutData as Record<string, string>)[lang] || (aboutData as Record<string, string>)['EN']}
              </p>
            </div>
            <div id="agent-col" style={{ flex: 1, minWidth: 280, borderLeft: '1px solid rgba(0,0,0,0.08)', paddingLeft: 48 }}>
              <SmartFilterDropdown 
                activeCategory={activeCategory} 
                setActiveCategory={setActiveCategory}
                onNavigate={(cat) => {
                  const slugMap: Record<string, string> = {
                    'Strings': 'strings',
                    'Ukuleles': 'ukuleles',
                    'Acoustic Guitars': 'acoustic-guitars',
                    'Electric Guitars': 'electric-guitars',
                    'Studio & Sound': 'studio-sound',
                    'Keyboards & Pianos': 'keyboards',
                    'Drum Kit': 'drums',
                  };
                  if (cat && slugMap[cat]) {
                    navigate('/shop/' + slugMap[cat]);
                  } else if (cat) {
                    navigate('/shop/' + cat.toLowerCase().replace(/[&\s]+/g, '-'));
                  } else {
                    navigate('/shop');
                  }
                }}
              />
              <p style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 900, color: '#0a0c0a', margin: '0 0 12px' }}>
                🎵 Ask Frankie - Our Music Advisor <span style={{ fontWeight: 400, fontSize: 11, letterSpacing: '0.06em', textTransform: 'none', color: 'rgba(10,12,10,0.5)' }}>(AI-powered)</span>
              </p>
              <SearchAgent currentLang={lang} />
            </div>
          </div>
        </div>
      </section>

      <FeaturedProducts activeCategory={activeCategory} />
    </>
  );
};


const SECTION = (title: string, content: string) => `
  <div style="marginBottom:'32px'">
    <h2 style="fontWeight:900;fontSize:'1.3rem';color:'#0a0c0a';margin:'0 0 12px';fontStyle:'italic';fontFamily:'Georgia,serif'">${title}</h2>
    <div style="fontSize:'14px';lineHeight:'1.8';color:'rgba(0,0,0,0.65)'">${content}</div>
  </div>
`;

const LegalPage: React.FC<{ title: string; version?: string; children: React.ReactNode }> = ({ title, version, children }) => (
  <div style={{ padding: '60px 40px 80px', maxWidth: 800, margin: '0 auto' }}>
    <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(2rem,5vw,3.2rem)', color: '#0a0c0a', margin: '0 0 8px', fontFamily: 'Georgia, serif' }}>{title}</h1>
    {version && <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(0,0,0,0.35)', margin: '0 0 32px' }}>{version}</p>}
    <div style={{ width: 48, height: 4, background: '#8B1A1A', margin: '0 0 40px' }} />
    {children}
    <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)', fontSize: 11, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      Essaness Music · No1 St Kieran's St · Kilkenny R95 CX89 · Ireland · info@essaness.com
    </div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 32 }}>
    <h2 style={{ fontWeight: 900, fontSize: '1.1rem', color: '#0a0c0a', margin: '0 0 10px', fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>{title}</h2>
    <div style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.65)' }}>{children}</div>
  </div>
);

const PrivacyPage: React.FC = () => (
  <LegalPage title="Privacy Policy" version="V1.0 · April 2026 · Essaness Music · Kilkenny">
    <p style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.65)', marginBottom: 32 }}>
      We respect your privacy and are committed to protecting your personal data in accordance with the General Data Protection Regulation (GDPR).
    </p>
    <Section title="1. Data Controller">
      <p>Essaness Music<br/>No1 St Kieran's St, Kilkenny R95 CX89, Ireland<br/>Email: <a href="mailto:info@essaness.com" style={{color:'#8B1A1A'}}>info@essaness.com</a><br/>Phone: +353 (0)56 776 5693<br/>VAT: IE1462723G</p>
      <p style={{marginTop:8}}>Digital infrastructure and data processing services are provided by DBOS (operating under Smart Trades Training, CRO No. 778363, Ireland) under a Data Processing Agreement.</p>
    </Section>
    <Section title="2. What Data We Collect">
      <p>When you use our website or make a purchase, we may collect: name, email address, phone number, shipping address, payment information (processed securely via Stripe — we never store card details), and website usage data.</p>
    </Section>
    <Section title="3. How We Use Your Data">
      <p>We process your data to: fulfil your orders and deliver products, respond to enquiries, process payments securely via Stripe, improve our website and service, and comply with legal obligations under Irish and EU law.</p>
    </Section>
    <Section title="4. Legal Basis">
      <p>We process data based on: contractual necessity (to fulfil your order), legitimate interest (to improve our service), legal obligations (GDPR, Irish consumer law), and your consent where required (analytics cookies).</p>
    </Section>
    <Section title="5. Data Sharing & Third-Party Processors">
      <p>We share data only with trusted processors required to deliver our service:</p>
      <ul style={{paddingLeft:20, marginTop:8}}>
        <li>Stripe — payment processing (Stripe Payments Europe Ltd., 1 Grand Canal Street Lower, Dublin 2)</li>
        <li>DPD Ireland — delivery services</li>
        <li>Google Analytics — website traffic analysis (activated on consent only)</li>
        <li>Microsoft Clarity — UX analysis (activated on consent only)</li>
        <li>DBOS — digital infrastructure and hosting</li>
      </ul>
      <p style={{marginTop:8}}>Data transfers to the US (Google) are safeguarded by Standard Contractual Clauses (SCCs).</p>
    </Section>
    <Section title="6. Data Retention">
      <p>Order data is retained for 7 years as required by Irish tax law. Marketing data is retained until you withdraw consent. Website usage data is retained for 26 months.</p>
    </Section>
    <Section title="7. Your Rights">
      <p>Under GDPR you have the right to: access your data, request correction or deletion, restrict or object to processing, and data portability. Contact us at <a href="mailto:info@essaness.com" style={{color:'#8B1A1A'}}>info@essaness.com</a> to exercise any right. You may also lodge a complaint with the Irish Data Protection Commission at <a href="https://www.dataprotection.ie" target="_blank" rel="noreferrer" style={{color:'#8B1A1A'}}>dataprotection.ie</a>.</p>
    </Section>
    <Section title="8. Cookies">
      <p>We use essential cookies for shop functionality and Stripe payments. Analytics cookies (Google Analytics, Microsoft Clarity) are only activated with your explicit consent. See our <a href="/cookies" style={{color:'#8B1A1A'}}>Cookie Policy</a> for details.</p>
    </Section>
    <Section title="9. Changes">
      <p>We may update this policy as needed. The latest version is always published on this page.</p>
    </Section>
  </LegalPage>
);

const CookiesPage: React.FC = () => (
  <LegalPage title="Cookie Policy" version="V1.0 · April 2026 · Essaness Music · Kilkenny">
    <p style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.65)', marginBottom: 32 }}>
      This page explains how Essaness Music uses cookies and similar technologies on our website.
    </p>
    <Section title="1. What Are Cookies?">
      <p>Cookies are small text files stored on your device when you visit a website. They help the site remember your preferences and improve your experience.</p>
    </Section>
    <Section title="2. Cookies We Use">
      <p><strong>Essential Cookies (always active)</strong><br/>Required for the shop to function: session management, shopping cart, Stripe payment security. These cannot be disabled.</p>
      <p style={{marginTop:12}}><strong>Analytics Cookies (consent required)</strong><br/>Google Analytics and Microsoft Clarity — used to understand how visitors use our site and improve performance. Only activated after you give consent via our cookie banner.</p>
      <p style={{marginTop:12}}><strong>Payment Cookies</strong><br/>Stripe sets cookies to ensure secure and compliant payment processing. These are essential for checkout.</p>
    </Section>
    <Section title="3. Legal Basis">
      <p>Essential cookies are used on the basis of legitimate interest. Analytics cookies require your explicit consent under GDPR. You may withdraw consent at any time by clearing your browser cookies or adjusting your browser settings.</p>
    </Section>
    <Section title="4. Managing Cookies">
      <p>You can manage or disable non-essential cookies through your browser settings at any time. Note that disabling essential cookies will affect shop functionality.</p>
    </Section>
    <Section title="5. Updates">
      <p>We may update this Cookie Policy periodically. The latest version is always available on this page.</p>
    </Section>
  </LegalPage>
);

const TermsPage: React.FC = () => (
  <LegalPage title="Terms & Conditions" version="V1.0 · April 2026 · Essaness Music · Kilkenny">
    <p style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.65)', marginBottom: 32 }}>
      These Terms & Conditions apply to all purchases made through the Essaness Music online shop.
    </p>
    <Section title="1. Seller">
      <p>Essaness Music, No1 St Kieran's St, Kilkenny R95 CX89, Ireland.<br/>Email: info@essaness.com · Phone: +353 (0)56 776 5693<br/>VAT No: IE1462723G</p>
    </Section>
    <Section title="2. Orders & Contract">
      <p>A binding contract is formed when you complete checkout and receive an order confirmation email. All prices include VAT at the applicable Irish rate.</p>
    </Section>
    <Section title="3. Payment">
      <p>All payments are processed securely via Stripe. We accept major credit and debit cards. Your card details are never stored by us.</p>
    </Section>
    <Section title="4. Shipping">
      <p>We deliver via DPD Ireland. Standard delivery times: Ireland 1–3 business days (€5.99), Northern Ireland 1–3 business days (€8.99), UK 2–4 business days (€29.99), Europe 5–10 business days (€29.99). Free in-store collection available.</p>
    </Section>
    <Section title="5. Right of Return — 14 Days">
      <p>Under EU consumer law, you have the right to return most items within 14 calendar days of receipt without giving a reason. To initiate a return, contact us at info@essaness.com. Items must be returned in original condition and packaging. Return shipping costs are the customer's responsibility unless the item is faulty.</p>
    </Section>
    <Section title="6. Hygiene Exception — Important">
      <p style={{background:'rgba(139,26,26,0.06)',border:'1px solid rgba(139,26,26,0.15)',padding:'12px 16px',borderLeft:'4px solid #8B1A1A'}}>
        <strong>Hygiene-sensitive items</strong> — including mouthpieces, reeds, recorders, tin whistles, harmonicas, and similar wind instrument accessories — <strong>cannot be returned once the seal has been broken or the item has been used</strong>, in accordance with EU Directive 2011/83/EU (Article 16(e)). This does not affect your statutory rights regarding faulty goods.
      </p>
    </Section>
    <Section title="7. Faulty Items">
      <p>If an item arrives damaged or faulty, contact us within 48 hours at info@essaness.com with photos. We will arrange a replacement or full refund at no cost to you.</p>
    </Section>
    <Section title="8. Limitation of Liability">
      <p>Our liability is limited to the value of the goods purchased. We are not liable for indirect loss, loss of profits, or third-party service failures (e.g. DPD delays).</p>
    </Section>
    <Section title="9. Governing Law">
      <p>These terms are governed by the laws of Ireland. Disputes may be referred to the European Online Dispute Resolution platform at <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" style={{color:'#8B1A1A'}}>ec.europa.eu/consumers/odr</a>.</p>
    </Section>
  </LegalPage>
);

const ShippingPage: React.FC = () => (
  <LegalPage title="Shipping & Returns" version="Essaness Music · Kilkenny · Ireland">
    <Section title="Delivery">
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
        <thead>
          <tr style={{borderBottom:'2px solid rgba(0,0,0,0.1)'}}>
            <th style={{textAlign:'left',padding:'8px 0',color:'rgba(0,0,0,0.5)',textTransform:'uppercase',letterSpacing:'0.08em',fontSize:10}}>Destination</th>
            <th style={{textAlign:'left',padding:'8px 0',color:'rgba(0,0,0,0.5)',textTransform:'uppercase',letterSpacing:'0.08em',fontSize:10}}>Carrier</th>
            <th style={{textAlign:'left',padding:'8px 0',color:'rgba(0,0,0,0.5)',textTransform:'uppercase',letterSpacing:'0.08em',fontSize:10}}>Time</th>
            <th style={{textAlign:'right',padding:'8px 0',color:'rgba(0,0,0,0.5)',textTransform:'uppercase',letterSpacing:'0.08em',fontSize:10}}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Ireland','DPD','1–3 business days','€5.99'],
            ['Northern Ireland','DPD','1–3 business days','€8.99'],
            ['UK','DPD','2–4 business days','€29.99'],
            ['Europe','DPD','5–10 business days','€29.99'],
            ['In-store Pickup','Kilkenny','Ready same day','Free'],
          ].map(([dest,carrier,time,cost]) => (
            <tr key={dest} style={{borderBottom:'1px solid rgba(0,0,0,0.06)'}}>
              <td style={{padding:'10px 0',fontWeight:600}}>{dest}</td>
              <td style={{padding:'10px 0',color:'rgba(0,0,0,0.5)'}}>{carrier}</td>
              <td style={{padding:'10px 0',color:'rgba(0,0,0,0.5)'}}>{time}</td>
              <td style={{padding:'10px 0',textAlign:'right',fontWeight:700,color:'#8B1A1A'}}>{cost}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{marginTop:16,fontSize:13,color:'rgba(0,0,0,0.5)'}}>Large or heavy items may incur an additional shipping surcharge of €15. This will be shown at checkout.</p>
    </Section>
    <Section title="Returns — 14 Day Right of Return">
      <p>You have 14 calendar days from receipt to return most items. Items must be unused and in original packaging. Contact <a href="mailto:info@essaness.com" style={{color:'#8B1A1A'}}>info@essaness.com</a> to arrange a return. Return shipping costs are the customer's responsibility unless the item is faulty.</p>
    </Section>
    <Section title="Hygiene Items — Cannot Be Returned">
      <div style={{background:'rgba(139,26,26,0.06)',border:'1px solid rgba(139,26,26,0.15)',padding:'12px 16px',borderLeft:'4px solid #8B1A1A'}}>
        <p><strong>The following items cannot be returned once opened or used:</strong></p>
        <ul style={{paddingLeft:20,marginTop:8}}>
          <li>Mouthpieces</li>
          <li>Reeds</li>
          <li>Recorders</li>
          <li>Tin whistles</li>
          <li>Harmonicas</li>
          <li>Any wind instrument accessories that make contact with the mouth</li>
        </ul>
        <p style={{marginTop:8}}>This applies under EU Directive 2011/83/EU Article 16(e) — hygiene protection. Your rights regarding faulty goods are not affected.</p>
      </div>
    </Section>
    <Section title="Faulty or Damaged Items">
      <p>Contact us within 48 hours of delivery at <a href="mailto:info@essaness.com" style={{color:'#8B1A1A'}}>info@essaness.com</a> with your order number and photos. We will arrange a replacement or full refund including return shipping.</p>
    </Section>
  </LegalPage>
);


const OurStoryPage: React.FC = () => {
  const chapters = [
    { id: 'origins', year: '1983', title: 'Ess & Ess — The Beginning', emoji: '🎸' },
    { id: 'teenage', year: '1990s', title: 'The Teenage Phase', emoji: '🎵' },
    { id: 'grow', year: '2000s', title: 'We Grow', emoji: '🌱' },
    { id: 'brands', year: '2010s', title: 'Brand Partnerships', emoji: '🤝🏽' },
    { id: 'shop', year: '~2015', title: 'A Kilkenny Institution', emoji: '📍' },
    { id: 'rebrand', year: '2016', title: 'Live Era & Rebrand', emoji: '🎤' },
    { id: 'digital', year: '2021', title: 'Digital Pivot', emoji: '💻' },
    { id: 'future', year: 'Now', title: 'Second Generation', emoji: '🔮' },
  ];

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ backgroundColor: '#F4F1E1', minHeight: '100vh' }}>
      <style>{`
        .story-section { scroll-margin-top: 80px; }
        :root { --story-nav-top: 156px; }
        @media(max-width:860px){ :root { --story-nav-top: 56px; } .story-section { scroll-margin-top: 56px; } }
        .chapter-nav-btn:hover { background: rgba(139,26,26,0.12) !important; color: #8B1A1A !important; }
        .img-placeholder { background: linear-gradient(135deg, #0a0c0a 0%, #1a1c1a 100%); display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.2); font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; }
      `}</style>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(180deg, #0a0c0a 0%, #1a1c1a 100%)', padding: '80px 40px 64px', textAlign: 'center' }}>
        <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.35)', margin: '0 0 16px' }}>Est. 1983 · Kilkenny City · Ireland</p>
        <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(2.5rem,6vw,5rem)', color: '#F4F1E1', margin: '0 0 16px', fontFamily: 'Georgia, serif', lineHeight: 1.05 }}>
          Our Story
        </h1>
        <div style={{ width: 52, height: 4, background: '#8B1A1A', margin: '0 auto 24px' }} />
        <p style={{ fontSize: 16, lineHeight: 1.8, color: 'rgba(255,255,255,0.6)', maxWidth: 640, margin: '0 auto 8px' }}>
          From a family passion to Kilkenny's most-loved music shop — the full 40-year journey of Essaness Music, built on love of music, family values, and the community of Kilkenny, Ireland.
        </p>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', margin: '12px 0 0' }}>"Essaness Music — Our Story, Go dtí do Scéal."</p>
      </div>

      {/* Chapter navigation — sticky below main header */}
      <div style={{ position: 'sticky', top: 'var(--story-nav-top,156px)', zIndex: 90, background: '#0a0c0a', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 24px', overflowX: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', gap: 0, minWidth: 'max-content' }}>
          {chapters.map(ch => (
            <button key={ch.id} className="chapter-nav-btn" onClick={() => scrollTo(ch.id)}
              style={{ background: 'none', border: 'none', borderBottom: '3px solid transparent', color: 'rgba(255,255,255,0.5)', padding: '14px 18px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
              {ch.emoji} {ch.year}
            </button>
          ))}
        </div>
      </div>

      {/* Intro */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '56px 40px 0' }}>
        <p style={{ fontSize: 16, lineHeight: 1.9, color: 'rgba(0,0,0,0.65)', fontStyle: 'italic', borderLeft: '4px solid #8B1A1A', paddingLeft: 24, marginBottom: 48 }}>
          There are businesses, and then there are institutions. Essaness Music — located at 1 Kieran Street in the heart of Kilkenny City — has become the latter. For over 40 years, this proudly family-run music shop has been the first port of call for musicians at every stage of their journey: from the nervous parent buying their child's first ukulele, to the seasoned gigging musician seeking a handmade Lowden acoustic. This is our story.
        </p>
      </div>

      {/* Chapters */}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 40px 80px' }}>

        {/* Chapter 1 */}
        <div id="origins" className="story-section" style={{ marginBottom: 72, paddingTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>1983 — Chapter One</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>Ess & Ess — The Binding of Two Good Families</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch1-1.jpg" alt="Essaness Music — Shop Front Early Years" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch1-2.jpg" alt="Essaness Music — Founders" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            The name 'Essaness' is not a marketing invention. It is a declaration. Ess and Ess — the binding of two good families, coming together with a shared belief that music belongs to everyone, and that Kilkenny deserved a shop to prove it.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            Founded in late 1985 on the historic Kieran Street at the heart of Kilkenny City, Essaness Music was built from day one on something far more powerful than capital investment: genuine passion for music and an unshakeable belief that every person deserves access to great instruments and honest, expert advice.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            From the very first day, Essaness offered the kind of intimate, personal service only a family business can. Customers were not account numbers. They were neighbours, students, musicians, and returning faces year after year. The founding philosophy, captured in a single line still used today, set the tone: <em>"Why not call in and take a test run on the instrument of your choice?"</em> That warmth has not dimmed across 40 years.
          </p>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['Founded 1983 — the name Essaness honours the union of two music-loving families','Located from the outset on Kieran Street, at the cultural core of medieval Kilkenny','Family-run from day one — built on personal passion, not corporate investment','Initial focus: guitars, traditional Irish instruments, and beginner starter packs'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Chapter 2 */}
        <div id="teenage" className="story-section" style={{ marginBottom: 72, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, marginTop: 24 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>1990s — Chapter Two</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>The Teenage Phase — Learning, Stretching, Growing</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch2-1.jpg" alt="Essaness Music — Traditional Instruments" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch2-2.jpg" alt="Essaness Music — SNS Sticker / Merch" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            Every great business has its growing pains, and Essaness was no different. The 1990s were the shop's teenage years — a decade of stretching boundaries, broadening the product range, and learning hard lessons about what customers truly needed.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            But Essaness was never just a shop. From early on, the families understood that their place in the music community was not behind a counter — it was at the gig, in the rehearsal room, on the stage. The SNS sticker became a badge of belonging, stamped across gear cases, van windows, and instrument bags at every Kilkenny gig. If you played in Kilkenny in the '90s and 2000s, you likely carried a piece of Essaness with you.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            This was also the era when Essaness began building the distributor relationships that would define its product range for decades. Yamaha, Cort, and Stentor partnerships were established — giving the shop access to reliable instruments at every price point.
          </p>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['Product range expanded to include electric guitars, drums, and keyboards','SNS merch introduced — stickers at every gig across Kilkenny','Yamaha and Cort dealerships established','Traditional Irish instruments became a cornerstone category','Growing reputation spreading into Waterford, Carlow, and Tipperary'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Chapter 3 */}
        <div id="grow" className="story-section" style={{ marginBottom: 72, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, marginTop: 24 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>2000s — Chapter Three</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>From Local Shop to Regional Music Destination</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch3-1.jpg" alt="Essaness Music — Lowden Guitars" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch3-2.jpg" alt="Essaness Music — Cort Range" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            The turn of the millennium marked a decisive shift in Essaness Music's trajectory. The shop's reputation had grown to the point where customers were making special trips from Waterford, Carlow, Tipperary, and further afield. This wasn't just a music shop anymore — it was a destination.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            The most significant step was securing a dealership for Lowden Handmade Irish Guitars — arguably the most prestigious acoustic guitar brand made on the island of Ireland. That Essaness achieved this spoke volumes about the quality of the operation built across the previous decade and a half.
          </p>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['Lowden Handmade Irish Guitars dealership established','Yamaha APX thin-body acoustic-electric became a top-seller across Leinster','National reputation established — customers travelling from multiple counties','First ecommerce website launched'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Chapter 4 */}
        <div id="brands" className="story-section" style={{ marginBottom: 72, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, marginTop: 24 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>2010s — Chapter Four</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>Bulk Orders, Band Deals & Music School Partnerships</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch4-1.jpg" alt="Essaness Music — Sheeran by Lowden" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch4-2.jpg" alt="Essaness Music — School Orders" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            One of the most defining chapters in the Essaness story was the moment the business began attracting institutional and professional customers alongside individual buyers. Music schools across County Kilkenny began placing bulk orders — a single school might order ten ukuleles at once for a classroom instrumental programme.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            The crowning achievement of this era was becoming an authorised stockist for Sheeran by Lowden — the accessible acoustic guitar range designed in collaboration with Ed Sheeran and the legendary Lowden workshop in Northern Ireland. Essaness was among the first stockists in the South of Ireland to carry the range.
          </p>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['Regular bulk school orders: 10+ ukuleles per classroom','Sheeran by Lowden guitars introduced — among first stockists in Leinster','PA hire service established for events across the South East','Instrument repair workshop expanded to all categories'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Chapter 5 */}
        <div id="shop" className="story-section" style={{ marginBottom: 72, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, marginTop: 24 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>~2015 — Chapter Five</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>A Permanent Home — A Kilkenny Institution</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch5-1.jpg" alt="Essaness Music — Kieran Street Shopfront" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch5-2.jpg" alt="Essaness Music — Inside the Shop" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            Kieran Street carries its own sense of history: named after St. Kieran, Kilkenny's patron saint, it is one of the most storied thoroughfares in a city full of storied streets. To plant a music shop flag here was to become part of Kilkenny's living cultural fabric — and Essaness did exactly that.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            The expanded premises allowed a full instrument display across all categories. The founding philosophy — try before you buy — could now be fully expressed. Customers were invited to pick up any instrument and play it. This hands-on ethos, combined with decades of staff expertise, created an in-store experience that no online retailer could replicate.
          </p>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['Permanent premises: 1 Kieran Street, Kilkenny (R95CX89)','Full instrument display floor — play before you buy','On-site repair workshop — all instrument categories','Multi-generational loyal customer base established','Phone: 056 776 5693 — personal service, no automated menus'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Chapter 6 */}
        <div id="rebrand" className="story-section" style={{ marginBottom: 72, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, marginTop: 24 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>2016 — Chapter Six</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>Live Era — Etching Music Into Memory</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch6-1.jpg" alt="Essaness Music — Live Music / PA Hire" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch6-2.jpg" alt="Essaness Music — Pre-Loved Gear" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            By 2016, Essaness had spent over three decades earning something rare: the deep, organic trust of a music community. The PA hire service was expanded and professionalised — Essaness gear was now powering local concerts, school productions, community festivals, and band showcases across Kilkenny and the Southeast.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            The pre-loved section deepened in this era. A guitar traded in by a gigging musician carried stories. The pre-loved rack at Essaness became its own kind of archive — instruments passing from one Kilkenny musician to the next, each one leaving a trace of its history. This was music as community, as connection, as living heritage.
          </p>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['PA hire professionalised — powering gigs, festivals, and school concerts','Pre-Loved Gear section deepened — instruments cycling through the community','Brand identity built around personal music bonding and live performance','Inclusive philosophy: welcoming to everyone, regardless of music level'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Chapter 7 */}
        <div id="digital" className="story-section" style={{ marginBottom: 72, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, marginTop: 24 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>2021 — Chapter Seven</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>Digital Reinvention — The Covid Pivot</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch7-1.jpg" alt="Essaness Music — New Website / Digital" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch7-2.jpg" alt="Essaness Music — Online Shop" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            When Covid-19 closed live music venues, shut down schools, and emptied Kieran Street in 2020, Essaness faced an existential test. The gigs stopped. The walk-in customers disappeared. The PA hire dried up. The question was not just how to survive, but how to stay connected to a community that was now entirely at home.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            The answer was a complete and purposeful digital reinvention. The 2021 rebrand brought the full Essaness experience online: a rebuilt essaness.com, nationwide delivery, online gift vouchers, and a content strategy that positioned the shop as a trusted music knowledge resource. The Essaness story was no longer just experienced in person on Kieran Street — it could be discovered by anyone in the world with a screen and a love of music.
          </p>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['essaness.com fully rebuilt — nationwide delivery across Ireland','New brand identity: user-friendly, inclusive, information-first','Pre-Loved Gear section online — accessible from anywhere','Online gift vouchers introduced','Instagram: 1,200+ followers · Facebook: 3,000 followers'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Chapter 8 */}
        <div id="future" className="story-section" style={{ marginBottom: 72, paddingTop: 24, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8, marginTop: 24 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#8B1A1A' }}>Now — Chapter Eight</span>
          </div>
          <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,2.6rem)', color: '#0a0c0a', margin: '0 0 20px', fontFamily: 'Georgia, serif', lineHeight: 1.1 }}>The Second Generation — The Future of Essaness Music</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch8-1.jpg" alt="Essaness Music — Second Generation" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            <img src="https://storage.googleapis.com/dbos-sentinel-backup-ie-0001/GIDO_IE_0001/media/story/story-ch8-2.jpg" alt="Essaness Music — Today's Shop" loading="lazy" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 16 }}>
            There is no more profound chapter in a family business story than when the next generation steps forward. For Essaness Music, that moment represents the ultimate vindication of everything the two founding families built: a business worth passing on, worth caring for, and worth carrying into the future with pride.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
            The original families' founding vision — music for everyone, from the first note to the finest instrument — has been not just preserved but amplified. Essaness Music today is as welcoming to the first-time player as it ever was, and as authoritative for the professional as any shop in Ireland. That is the real legacy of Ess and Ess: two families, four decades, one extraordinary institution.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', marginBottom: 24 }}>
              And the story is still being written. In 2026, Essaness Music opens its next chapter: a brand-new online home, rebuilt from the ground up on the DBOS digital platform. More than a website, it brings the feel of Kieran Street to anyone, anywhere — the full range easier to explore than ever, with an intelligent Music Advisor on hand to help every player find the right instrument, in any language. Forty years on, the founding promise is unchanged — expert, honest, welcoming — now offered to the world as warmly as it's always been offered across the counter.
            </p>
            <div style={{ background: '#0a0c0a', padding: '24px 28px', marginBottom: 24 }}>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', margin: 0 }}>
              "Ess and Ess. Two families. One shop. Forty-two years of music, community, and connection. Whether you step through the door on Kieran Street, browse the shop online, or simply carry an old SNS sticker on your instrument case — you are part of what Essaness has always been: a place where music belongs to everyone."
            </p>
          </div>
          <div style={{ background: 'rgba(139,26,26,0.06)', borderLeft: '4px solid #8B1A1A', padding: '16px 20px' }}>
            <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: '#8B1A1A', margin: '0 0 8px' }}>Key Milestones</p>
            {['Digital marketing capabilities dramatically expanded','New instrument lines: Ibanez JEM, ESP LTD Mirage Deluxe, Spira series','40-year anniversary celebrated with the Kilkenny community','Continued authorised stockist: Lowden, Sheeran by Lowden, Cort, Yamaha','The founding spirit carried forward — inclusive, expert, community-first','New ecommerce platform launched 2026 — built on DBOS, with multilingual AI Music Advisor'].map((item, i) => (
              <p key={i} style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(0,0,0,0.6)', margin: '0 0 4px' }}>· {item}</p>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div style={{ background: '#0a0c0a', padding: '40px', marginBottom: 48 }}>
          <h3 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: '1.4rem', color: '#F4F1E1', margin: '0 0 24px', fontFamily: 'Georgia, serif' }}>42 Years at a Glance</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
            {[
              { year: '1983', label: 'Founded', desc: 'Ess & Ess — two families, one vision' },
              { year: '1990s', label: 'Teenage Phase', desc: 'Expansion, SNS stickers, first partnerships' },
              { year: '2000s', label: 'National Reach', desc: 'Lowden dealership, customers from across Ireland' },
              { year: '2010s', label: 'Trade Partner', desc: 'School bulk orders, Sheeran by Lowden' },
              { year: '~2015', label: 'Institution', desc: 'Permanent home on Kieran Street' },
              { year: '2016', label: 'Live Era', desc: 'PA hire, pre-loved, community bond' },
              { year: '2021', label: 'Digital Pivot', desc: 'Covid drives online transformation' },
              { year: 'Now', label: '2nd Generation', desc: 'Heritage preserved, future secured' },
            ].map(item => (
              <div key={item.year} style={{ borderLeft: '2px solid rgba(139,26,26,0.4)', paddingLeft: 14 }}>
                <p style={{ fontSize: 13, fontWeight: 900, color: '#8B1A1A', margin: '0 0 2px' }}>{item.year}</p>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#F4F1E1', margin: '0 0 4px' }}>{item.label}</p>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', padding: '20px 0 40px' }}>
          <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(0,0,0,0.35)', margin: '0 0 16px' }}>Come and Be Part of the Story</p>
          <p style={{ fontSize: 20, fontStyle: 'italic', fontWeight: 900, color: '#0a0c0a', fontFamily: 'Georgia, serif', margin: '0 0 24px' }}>"For All Your Music Needs — For Over 40 Years."</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/shop" style={{ background: '#8B1A1A', color: 'white', padding: '13px 24px', textDecoration: 'none', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em' }}>Browse the Shop</a>
            <a href="tel:+353567765693" style={{ background: 'transparent', color: '#0a0c0a', border: '1px solid rgba(0,0,0,0.2)', padding: '13px 24px', textDecoration: 'none', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em' }}>📞 Call Us</a>
          </div>
          <p style={{ fontSize: 12, color: 'rgba(0,0,0,0.4)', marginTop: 20 }}>📍 1 Kieran Street, Kilkenny · R95CX89 · Ireland</p>
        </div>

      </div>
    </div>
  );
};



const generateCustomerGID = () => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substr(2, 4).toUpperCase();
  return "CUST_IE_" + timestamp + random;
};

const ContactPage: React.FC = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", newsletter: false, privacy: false });
  const [status, setStatus] = useState<"idle"|"sending"|"sent"|"error">("idle");
  const [customerGid, setCustomerGid] = useState("");

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.privacy) { setStatus("error"); return; }
    setStatus("sending");
    try {
      const gid = generateCustomerGID();
      setCustomerGid(gid);

      // Save to Firestore via agent + email via Workspace (Phase II)
      await fetch("https://essaness-search-agent-273538020820.europe-west1.run.app/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gid, client_id: "GIDO_IE_0001", name: form.name, email: form.email, phone: form.phone, message: form.message, newsletter: form.newsletter, source: "contact_form" })
      });

      setStatus("sent");
    } catch(e) { setStatus("error"); }
  };

  return (
    <div style={{ backgroundColor: "#F4F1E1", minHeight: "100vh" }}>
      <div style={{ background: "radial-gradient(ellipse 90% 70% at 50% 0%, rgba(139,26,26,0.10) 0%, rgba(244,241,225,0) 70%), #F4F1E1", padding: "60px 40px 48px" }}>
        <h1 style={{ fontWeight: 900, fontStyle: "italic", fontSize: "clamp(2rem,5vw,3.5rem)", color: "#0a0c0a", margin: "0 0 14px", fontFamily: "Georgia, serif" }}>Get in Touch</h1>
        <div style={{ width: 48, height: 4, background: "#8B1A1A", margin: "0 0 40px" }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, maxWidth: 1000 }}>

          <div>
            <div style={{ marginBottom: 32 }}>
              <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(0,0,0,0.4)", fontWeight: 700, margin: "0 0 12px" }}>Visit Us</p>
              <p style={{ fontSize: 15, lineHeight: 1.8, color: "rgba(0,0,0,0.65)" }}>No1 St Kieran's St<br/>Kilkenny City<br/>R95 CX89, Ireland</p>
            </div>
            <div style={{ marginBottom: 32 }}>
              <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(0,0,0,0.4)", fontWeight: 700, margin: "0 0 12px" }}>Call or Email</p>
              <a href="tel:+353567765693" style={{ display: "block", fontSize: 18, fontWeight: 900, color: "#8B1A1A", textDecoration: "none", marginBottom: 8 }}>+353 (0)56 776 5693</a>
              <a href="mailto:info@essaness.com" style={{ fontSize: 14, color: "#8B1A1A", textDecoration: "none" }}>info@essaness.com</a>
            </div>
            <div style={{ marginBottom: 32 }}>
              <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(0,0,0,0.4)", fontWeight: 700, margin: "0 0 12px" }}>Opening Hours</p>
              {[["Mon-Wed, Fri", "10:00 - 17:30"],["Thursday", "10:00 - 18:00"],["Saturday", "10:00 - 18:00"],["Sunday", "Closed"]].map(([day, hours]) => (
                <div key={day} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 13, maxWidth: 280 }}>
                  <span style={{ fontWeight: 600 }}>{day}</span>
                  <span style={{ color: hours === "Closed" ? "#8B1A1A" : "rgba(0,0,0,0.5)" }}>{hours}</span>
                </div>
              ))}
            </div>
            <a href="https://www.google.com/maps/dir/?api=1&destination=52.6517554,-7.2513701" target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#0a0c0a", color: "white", padding: "12px 20px", textDecoration: "none", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Get Directions
            </a>
          </div>

          <div>
            {status === "sent" ? (
              <div style={{ padding: "40px", background: "white", border: "1px solid rgba(0,0,0,0.08)", textAlign: "center" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🎸</div>
                <h3 style={{ fontWeight: 900, fontStyle: "italic", fontSize: "1.5rem", color: "#0a0c0a", margin: "0 0 8px", fontFamily: "Georgia, serif" }}>Message sent!</h3>
                <p style={{ fontSize: 13, color: "rgba(0,0,0,0.5)", margin: "0 0 16px" }}>We will be in touch soon. Your reference: <strong>{customerGid}</strong></p>
                <button onClick={() => setStatus("idle")} style={{ background: "#8B1A1A", color: "white", border: "none", padding: "10px 20px", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", cursor: "pointer" }}>Send Another</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(0,0,0,0.4)", fontWeight: 700, margin: 0 }}>Send a Message</p>
                {[
                  { id: "name", label: "Full Name *", type: "text", placeholder: "Your name" },
                  { id: "email", label: "Email Address *", type: "email", placeholder: "your@email.com" },
                  { id: "phone", label: "Phone Number", type: "tel", placeholder: "+353..." },
                ].map(field => (
                  <div key={field.id}>
                    <label style={{ display: "block", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(0,0,0,0.45)", fontWeight: 700, marginBottom: 6 }}>{field.label}</label>
                    <input type={field.type} placeholder={field.placeholder} value={(form as any)[field.id]}
                      onChange={e => setForm({...form, [field.id]: e.target.value})}
                      style={{ width: "100%", background: "white", border: "1px solid rgba(0,0,0,0.15)", padding: "10px 14px", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
                  </div>
                ))}
                <div>
                  <label style={{ display: "block", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(0,0,0,0.45)", fontWeight: 700, marginBottom: 6 }}>Message</label>
                  <textarea placeholder="How can we help?" value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                    rows={4} style={{ width: "100%", background: "white", border: "1px solid rgba(0,0,0,0.15)", padding: "10px 14px", fontSize: 13, outline: "none", fontFamily: "inherit", resize: "vertical" }} />
                </div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.newsletter} onChange={e => setForm({...form, newsletter: e.target.checked})} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", lineHeight: 1.5 }}>Sign me up for news, offers and updates from Essaness Music</span>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.privacy} onChange={e => setForm({...form, privacy: e.target.checked})} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", lineHeight: 1.5 }}>I agree to the <a href="/privacy" style={{color:"#8B1A1A"}}>Privacy Policy</a> and consent to Essaness Music storing my details. *</span>
                </label>
                {status === "error" && (
                  <p style={{ fontSize: 11, color: "#8B1A1A", textTransform: "uppercase", letterSpacing: "0.08em" }}>Please fill in your name, email and accept the privacy policy.</p>
                )}
                <button onClick={handleSubmit} disabled={status === "sending"}
                  style={{ background: "#8B1A1A", color: "white", border: "none", padding: "14px", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em", cursor: "pointer" }}>
                  {status === "sending" ? "Sending..." : "Send Message"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};



const RentalPage: React.FC = () => {
  const [openTile, setOpenTile] = useState<string | null>(null);

  const tiles = [
    {
      id: 'guitars',
      icon: '🎸',
      title: 'Guitars & Bass',
      short: 'Acoustic, electric and bass guitars for gigs, sessions and recording.',
      detail: 'Whether you need a quality acoustic for an intimate session, an electric for a gig, or a bass to lock down the rhythm section — we have you covered. All instruments are professionally set up and ready to play. Perfect for weekend gigs, studio sessions, or trying before you buy.',
      price: 'From €15/day · €35/weekend',
      available: ['Acoustic Guitars', 'Electric Guitars', 'Bass Guitars', 'Amplifiers'],
    },
    {
      id: 'traditional',
      icon: '🪗',
      title: 'Traditional & Folk',
      short: 'Bodhráns, tin whistles, concertinas and more for sessions and events.',
      detail: 'From Kilkenny to Connemara — traditional Irish music is at the heart of what we do. Rent a bodhrán for a céilí, a concertina for a festival, or a full set of tin whistles for a school event. We stock instruments suited to all levels, from complete beginners to seasoned session musicians.',
      price: 'From €10/day · €25/weekend',
      available: ['Bodhrán', 'Tin Whistles', 'Concertina', 'Mandolin', 'Bouzouki'],
    },
    {
      id: 'keyboards',
      icon: '🎹',
      title: 'Keyboards & Pianos',
      short: 'Digital pianos and keyboards for events, rehearsals and recording.',
      detail: 'Need a quality keyboard for a wedding, corporate event, school concert or studio session? We offer digital pianos with weighted keys and a natural piano feel. Ideal for rehearsals, one-off performances, or extended practice periods. Delivery and collection available in the Kilkenny area.',
      price: 'From €25/day · €55/weekend',
      available: ['Casio Digital Pianos', 'Stage Keyboards', 'MIDI Controllers'],
    },
    {
      id: 'studio',
      icon: '🎙️',
      title: 'Studio & PA Equipment',
      short: 'Microphones, speakers, mixers and PA gear for events and recording.',
      detail: 'From a single Shure SM58 for a vocalist to a full PA setup for a community event — we can help. Our studio and PA rental covers microphones, speakers, audio interfaces and more. Ideal for gigs, weddings, corporate events, school performances and home recording sessions.',
      price: 'From €20/day · €45/weekend',
      available: ['Shure SM58 Microphones', 'PA Speakers', 'Audio Interfaces', 'Mixers', 'Cables & Stands'],
    },
    {
      id: 'drums',
      icon: '🥁',
      title: 'Drums & Percussion',
      short: 'Full drum kits, cajon and percussion for rehearsals and gigs.',
      detail: 'Drum kits are expensive to own and difficult to transport — which makes rental the smart choice for many musicians. We offer full acoustic kits for rehearsal spaces and venues, as well as cajon and hand percussion for more intimate settings. Available for daily or weekend rental.',
      price: 'From €30/day · €65/weekend',
      available: ['Full Drum Kits', 'Cajon', 'Samba Percussion', 'Cymbals & Hardware'],
    },
    {
      id: 'buysellback',
      icon: '🔄',
      title: 'Try, Buy & Sell Back',
      short: 'Our unique buy-back programme — invest in music without the long-term commitment.',
      detail: 'Not ready to commit to owning an instrument? Buy it from us, use it for a season, and sell it back to us at an agreed price. This is ideal for students, parents unsure if lessons will stick, or musicians trying a new instrument. We call it the Essaness Buy-Back Programme — music without the risk.',
      price: 'Ask in store for current buy-back terms',
      available: ['Most new instruments eligible', 'Condition assessment required', 'Agreed buy-back price at time of purchase'],
      highlight: true,
    },
  ];

  return (
    <div style={{ backgroundColor: '#F4F1E1', minHeight: '100vh' }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        .tile-detail { animation: fadeIn 0.25s ease-out; }
      `}</style>

      {/* Hero */}
      <div style={{
        background: 'radial-gradient(ellipse 90% 70% at 50% 0%, rgba(139,26,26,0.12) 0%, rgba(244,241,225,0) 70%), #F4F1E1',
        padding: '60px 40px 48px',
      }}>
        <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(0,0,0,0.35)', margin: '0 0 12px' }}>Essaness Music · Kilkenny · Est. 1986</p>
        <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.8rem,3.5vw,3rem)', color: '#0a0c0a', margin: '0 0 16px', fontFamily: 'Georgia, serif', lineHeight: 1.05 }}>
          Rent an Instrument.<br/>Play Tonight.
        </h1>
        <div style={{ width: 52, height: 4, background: '#8B1A1A', margin: '0 0 24px' }} />
        <p style={{ fontSize: 15, lineHeight: 1.8, color: 'rgba(0,0,0,0.62)', maxWidth: 640, margin: '0 0 32px' }}>
          Whether it's a weekend gig, a one-off session, a school concert or a studio day — Essaness Music has the instrument you need, ready to play. Based in the heart of Kilkenny since 1986, we offer short-term rentals with no long-term commitment. And if you fall in love with an instrument, our unique <strong>Buy-Back Programme</strong> means you can own without the risk.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="tel:+353567765693" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#8B1A1A', color: 'white', padding: '13px 24px', textDecoration: 'none', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            📞 Call to Book — 056 776 5693
          </a>
          <a href="mailto:info@essaness.com" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', color: '#0a0c0a', border: '1px solid rgba(0,0,0,0.2)', padding: '13px 24px', textDecoration: 'none', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            ✉️ info@essaness.com
          </a>
        </div>
      </div>

      {/* Key info strip */}
      <div style={{ background: '#0a0c0a', padding: '20px 40px', display: 'flex', gap: 40, flexWrap: 'wrap' }}>
        {[
          ['📅', 'Daily & Weekend', 'No long-term lock-in'],
          ['🚗', 'Collect in Store', "No1 St Kieran's St, Kilkenny"],
          ['🔄', 'Buy-Back Option', 'Try before you commit'],
          ['🎓', 'Beginner Friendly', "We'll show you how to use it"],
        ].map(([icon, title, sub]) => (
          <div key={title} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22 }}>{icon}</span>
            <div>
              <p style={{ color: '#F4F1E1', fontWeight: 700, fontSize: 13, margin: 0 }}>{title}</p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>{sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Rental tiles */}
      <div style={{ padding: '48px 40px 80px' }}>
        <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: '1.8rem', color: '#0a0c0a', margin: '0 0 8px', fontFamily: 'Georgia, serif' }}>What Can You Rent?</h2>
        <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)', margin: '0 0 32px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Click any category to see details</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {tiles.map(tile => (
            <div key={tile.id}
              style={{
                background: tile.highlight ? '#0a0c0a' : 'white',
                border: `1px solid ${tile.highlight ? 'rgba(139,26,26,0.4)' : 'rgba(0,0,0,0.08)'}`,
                boxShadow: openTile === tile.id
                  ? '0 0 0 1px rgba(139,26,26,0.3), 0 8px 32px rgba(0,0,0,0.15)'
                  : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'box-shadow 0.3s ease',
                cursor: 'pointer',
              }}
              onClick={() => setOpenTile(openTile === tile.id ? null : tile.id)}>

              <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ fontSize: 28 }}>{tile.icon}</span>
                  <div>
                    <p style={{ fontWeight: 900, fontSize: 15, color: tile.highlight ? '#F4F1E1' : '#0a0c0a', margin: '0 0 2px', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{tile.title}</p>
                    <p style={{ fontSize: 11, color: tile.highlight ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', margin: 0 }}>{tile.short}</p>
                  </div>
                </div>
                <span style={{ color: tile.highlight ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)', fontSize: 18, transition: 'transform 0.2s', transform: openTile === tile.id ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>▾</span>
              </div>

              {openTile === tile.id && (
                <div className="tile-detail" style={{ borderTop: `1px solid ${tile.highlight ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`, padding: '20px 24px' }}>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: tile.highlight ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.65)', margin: '0 0 16px' }}>{tile.detail}</p>
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: tile.highlight ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)', margin: '0 0 8px', fontWeight: 700 }}>Available</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {tile.available.map(item => (
                        <span key={item} style={{ fontSize: 11, padding: '3px 10px', background: tile.highlight ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: tile.highlight ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', border: `1px solid ${tile.highlight ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>{item}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <p style={{ fontWeight: 900, fontSize: 15, color: '#8B1A1A', margin: 0 }}>{tile.price}</p>
                    <a href="tel:+353567765693"
                      style={{ background: '#8B1A1A', color: 'white', padding: '9px 18px', textDecoration: 'none', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                      onClick={e => e.stopPropagation()}>
                      Book Now →
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{ marginTop: 56, background: '#0a0c0a', padding: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 24 }}>
          <div>
            <h3 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: '1.6rem', color: '#F4F1E1', margin: '0 0 8px', fontFamily: 'Georgia, serif' }}>Not sure what you need?</h3>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: 0 }}>Call us and we'll sort you out. That's what we're here for — since 1986.</p>
          </div>
          <a href="tel:+353567765693" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#8B1A1A', color: 'white', padding: '14px 28px', textDecoration: 'none', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>
            📞 056 776 5693
          </a>
        </div>
      </div>
    </div>
  );
};


// ============================================
// BLOG SYSTEM
// ============================================
interface BlogPost {
  gid: string;
  slug: string;
  title: string;
  author: string;
  date: string;
  category: string;
  read_time: number;
  summary: string;
  hero_image: string;
  tags: string[];
  content: string;
  published: boolean;
}


const BlogEmailCapture: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle'|'sent'|'error'>('idle');

  const handleSubmit = async () => {
    if (!email) return;
    try {
      await fetch('https://essaness-search-agent-273538020820.europe-west1.run.app/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gid: 'BLOG_' + Date.now().toString(36).toUpperCase(),
          client_id: 'GIDO_IE_0001',
          email,
          source: 'blog_signup',
          newsletter: true,
        })
      });
      setStatus('sent');
    } catch { setStatus('error'); }
  };

  return (
    <div style={{ background: '#0a0c0a', padding: '28px 32px', marginTop: 40, borderLeft: '4px solid #8B1A1A' }}>
      <p style={{ color: '#F4F1E1', fontWeight: 900, fontStyle: 'italic', fontSize: '1.2rem', margin: '0 0 6px', fontFamily: 'Georgia, serif' }}>Enjoyed this article?</p>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, margin: '0 0 16px' }}>Get music tips, news and offers from Essaness Music Kilkenny — straight to your inbox.</p>
      {status === 'sent' ? (
        <p style={{ color: '#4CAF50', fontWeight: 700, fontSize: 13 }}>🎸 You are in! Thanks for subscribing.</p>
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)}
            style={{ flex: 1, minWidth: 200, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#F4F1E1', padding: '10px 14px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
          <button onClick={handleSubmit}
            style={{ background: '#8B1A1A', color: 'white', border: 'none', padding: '10px 20px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>
            Subscribe
          </button>
        </div>
      )}
    </div>
  );
};

const formatContent = (content: string) => {
  return content.split('\n\n').map((para, i) => {
    if (para.startsWith('## ')) {
      return <h2 key={i} style={{ fontWeight: 900, fontStyle: 'italic', fontSize: '1.4rem', color: '#0a0c0a', margin: '32px 0 12px', fontFamily: 'Georgia, serif', borderBottom: '2px solid rgba(139,26,26,0.2)', paddingBottom: 8 }}>{para.replace('## ', '')}</h2>;
    }
    if (para.startsWith('**') && para.endsWith('**')) {
      return <p key={i} style={{ fontWeight: 700, fontSize: 15, color: '#0a0c0a', margin: '16px 0 8px' }}>{para.replace(/\*\*/g, '')}</p>;
    }
    if (para.startsWith('- ') || para.includes('\n- ')) {
      const items = para.split('\n').filter(l => l.startsWith('- '));
      return (
        <ul key={i} style={{ paddingLeft: 24, margin: '12px 0' }}>
          {items.map((item, j) => (
            <li key={j} style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.7)', marginBottom: 4 }}>{item.replace('- ', '')}</li>
          ))}
        </ul>
      );
    }
    if (para.startsWith('**Q:')) {
      const [q, a] = para.split('\nA: ');
      return (
        <div key={i} style={{ background: 'rgba(139,26,26,0.04)', border: '1px solid rgba(139,26,26,0.12)', padding: '16px 20px', margin: '16px 0', borderLeft: '4px solid #8B1A1A' }}>
          <p style={{ fontWeight: 700, fontSize: 14, color: '#8B1A1A', margin: '0 0 6px' }}>{q.replace(/\*\*/g, '')}</p>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: 'rgba(0,0,0,0.65)', margin: 0 }}>{a}</p>
        </div>
      );
    }
    return <p key={i} style={{ fontSize: 15, lineHeight: 1.85, color: 'rgba(0,0,0,0.68)', margin: '0 0 16px' }}>{para}</p>;
  });
};

const BlogListPage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const q = query(
          collection(db, 'blog_posts'),
          where('published', '==', true),
          where('client_id', '==', 'GIDO_IE_0001')
        );
        const snap = await getDocs(q);
        const loaded = snap.docs.map(d => ({ ...d.data(), gid: d.id } as BlogPost));
        // Sort by date descending in JS — no composite index needed
        loaded.sort((a, b) => b.date.localeCompare(a.date));
        setPosts(loaded);
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    loadPosts();
  }, []);

  return (
    <div style={{ backgroundColor: '#F4F1E1', minHeight: '100vh' }}>
      <div style={{ background: 'radial-gradient(ellipse 90% 60% at 50% 0%, rgba(139,26,26,0.08) 0%, transparent 70%), #F4F1E1', padding: '60px 40px 48px', maxWidth: 1100, margin: '0 auto' }}>
        <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(0,0,0,0.35)', margin: '0 0 10px' }}>Essaness Music · Kilkenny · Est. 1986</p>
        <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(2rem,5vw,3.5rem)', color: '#0a0c0a', margin: '0 0 10px', fontFamily: 'Georgia, serif' }}>Music Know-How</h1>
        <div style={{ width: 48, height: 4, background: '#8B1A1A', margin: '0 0 16px' }} />
        <p style={{ fontSize: 14, lineHeight: 1.8, color: 'rgba(0,0,0,0.55)', maxWidth: 600, margin: 0 }}>Tips, techniques, and stories from Glen Salmon and the Essaness Music team — over 40 years of expertise, shared freely.</p>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 40px 80px' }}>
        {loading ? (
          <p style={{ color: 'rgba(0,0,0,0.4)', fontSize: 13 }}>Loading articles...</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 }}>
            {posts.map(post => (
              <div key={post.gid} onClick={() => navigate(`/blog/${post.slug}`)}
                style={{ background: 'white', border: '1px solid rgba(0,0,0,0.07)', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ height: 180, background: post.hero_image ? `url(${post.hero_image}) center/cover` : 'linear-gradient(135deg, #0a0c0a 0%, #1a1c1a 50%, #2a1a1a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {!post.hero_image && <span style={{ fontSize: 48 }}>🎸</span>}
                </div>
                <div style={{ padding: '20px 24px 24px' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em', background: 'rgba(139,26,26,0.1)', color: '#8B1A1A', padding: '3px 8px', fontWeight: 700 }}>{post.category}</span>
                    <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,0,0,0.35)', padding: '3px 0' }}>{post.read_time} min read</span>
                  </div>
                  <h2 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: '1.05rem', color: '#0a0c0a', margin: '0 0 10px', fontFamily: 'Georgia, serif', lineHeight: 1.3 }}>{post.title}</h2>
                  <p style={{ fontSize: 12, lineHeight: 1.7, color: 'rgba(0,0,0,0.55)', margin: '0 0 16px' }}>{post.summary.substring(0, 120)}...</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{post.author} · {post.date}</span>
                    <span style={{ fontSize: 11, color: '#8B1A1A', fontWeight: 700 }}>Read →</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const ShareButtons: React.FC<{ title: string; url: string }> = ({ title, url }) => {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 24 }}>
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,0,0,0.4)', fontWeight: 700 }}>Share:</span>
      <a href={"https://wa.me/?text=" + encodedTitle + "%20" + encodedUrl} target="_blank" rel="noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#25D366', color: 'white', padding: '6px 14px', textDecoration: 'none', fontSize: 11, fontWeight: 700, borderRadius: 2 }}>
        💬 WhatsApp
      </a>
      <a href={"https://www.facebook.com/sharer/sharer.php?u=" + encodedUrl} target="_blank" rel="noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1877F2', color: 'white', padding: '6px 14px', textDecoration: 'none', fontSize: 11, fontWeight: 700, borderRadius: 2 }}>
        📘 Facebook
      </a>
      <button onClick={copyLink}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#E1306C', color: 'white', padding: '6px 14px', border: 'none', fontSize: 11, fontWeight: 700, borderRadius: 2, cursor: 'pointer' }}>
        📸 {copied ? 'Copied for Instagram!' : 'Instagram'}
      </button>
      <a href={"mailto:?subject=" + encodedTitle + "&body=Check this out: " + encodedUrl}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#0a0c0a', color: 'white', padding: '6px 14px', textDecoration: 'none', fontSize: 11, fontWeight: 700, borderRadius: 2 }}>
        ✉️ Email
      </a>
      <button onClick={copyLink}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.08)', color: '#0a0c0a', padding: '6px 14px', border: '1px solid rgba(0,0,0,0.15)', fontSize: 11, fontWeight: 700, borderRadius: 2, cursor: 'pointer' }}>
        🔗 {copied ? 'Copied!' : 'Copy Link'}
      </button>
      <div style={{ width: '100%', height: 1, background: 'rgba(0,0,0,0.08)', margin: '8px 0 4px' }} />
      <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,0,0,0.4)', fontWeight: 700 }}>Follow Essaness:</span>
      <a href="https://www.facebook.com/essanessmusic" target="_blank" rel="noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1877F2', color: 'white', padding: '6px 14px', textDecoration: 'none', fontSize: 11, fontWeight: 700, borderRadius: 2 }}>
        📘 Facebook Page
      </a>
      <a href="https://www.instagram.com/essaness_music" target="_blank" rel="noreferrer"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: 'white', padding: '6px 14px', textDecoration: 'none', fontSize: 11, fontWeight: 700, borderRadius: 2 }}>
        📸 Instagram
      </a>
    </div>
  );
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUrl = `https://essanessmusic.ie/blog/${slug}`;

  useEffect(() => {
    const loadPost = async () => {
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const q = query(
          collection(db, 'blog_posts'),
          where('slug', '==', slug),
          where('client_id', '==', 'GIDO_IE_0001')
        );
        const snap = await getDocs(q);
        if (!snap.empty) setPost({ ...snap.docs[0].data(), gid: snap.docs[0].id } as BlogPost);
      } catch(e) { console.error(e); }
      setLoading(false);
    };
    loadPost();
  }, [slug]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'rgba(0,0,0,0.4)' }}>Loading...</div>;
  if (!post) return <div style={{ padding: 60, textAlign: 'center' }}>Article not found. <button onClick={() => navigate('/blog')} style={{ color: '#8B1A1A', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>← Back to Blog</button></div>;

  return (
    <div style={{ backgroundColor: '#F4F1E1', minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ height: 320, background: post.hero_image ? `url(${post.hero_image}) center/cover` : 'linear-gradient(135deg, #0a0c0a 0%, #1a1c1a 50%, #2a1a1a 100%)', display: 'flex', alignItems: 'flex-end', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)' }} />
        <div style={{ position: 'relative', padding: '0 48px 32px', width: '100%' }}>
          <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.14em', background: '#8B1A1A', color: 'white', padding: '4px 10px', fontWeight: 700, marginBottom: 12, display: 'inline-block' }}>{post.category}</span>
          <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.6rem,4vw,3rem)', color: 'white', margin: '8px 0 0', fontFamily: 'Georgia, serif', lineHeight: 1.1, maxWidth: 800 }}>{post.title}</h1>
        </div>
      </div>

      {/* Meta */}
      <div style={{ background: '#0a0c0a', padding: '12px 48px', display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>By {post.author}</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>·</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{post.date}</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>·</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{post.read_time} min read</span>
        <button onClick={() => navigate('/blog')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 11, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em' }}>← All Articles</button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 40px 80px' }}>
        {/* Summary */}
        <p style={{ fontSize: 16, lineHeight: 1.8, color: 'rgba(0,0,0,0.6)', fontStyle: 'italic', borderLeft: '4px solid #8B1A1A', paddingLeft: 20, margin: '0 0 40px' }}>{post.summary}</p>

        {/* Tags */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 32 }}>
          {(post.tags || []).map(tag => (
            <span key={tag} style={{ fontSize: 10, padding: '3px 10px', background: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', border: '1px solid rgba(0,0,0,0.1)' }}>{tag}</span>
          ))}
        </div>

        {/* Article content */}
        <div>{formatContent(post.content)}</div>

        {/* Share */}
        <ShareButtons title={post.title} url={currentUrl} />

        {/* Email capture */}
        <BlogEmailCapture />
      </div>
    </div>
  );
};


// ============================================
// BLOG SYSTEM
// ============================================







const App: React.FC = () => {
  const [lang, setLang] = useState('EN');
  return (
    <BrowserRouter>
      <Layout lang={lang} setLang={setLang}>
        <Routes>
          <Route path="/" element={<HomePage lang={lang} />} />
          <Route path="/shop" element={<ShopPage lang={lang} />} />
          <Route path="/shop/:category" element={<ShopPage lang={lang} />} />
          <Route path="/product/:slug" element={<ProductPage lang={lang} />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/rental" element={<RentalPage />} />
          <Route path="/events" element={<StaticPage title="Events" subtitle="Upcoming events, workshops and Essaness Day in Kilkenny." />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/our-story" element={<OurStoryPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/shipping" element={<ShippingPage />} />
          <Route path="*" element={<StaticPage title="Page not found" subtitle="Sorry, this page does not exist." />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
};

export default App;
